import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { B400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { View } from '../homeRouteParams';
import { TaskNavigationItem } from '../HomeViews/Tasks/TaskNavigationItem';
import { NAV_COLLAPSE_BREAKPOINT_PX } from '../layoutConstants';
import { LazyLeftPanelExtendedItems } from '../LeftPanel/index';

import { HomeIcon } from './images/HomeIcon';
import { RecentIcon } from './images/RecentIcon';
import { DraftIcon } from './images/DraftIcon';
import { StarIcon } from './images/StarIcon';
import { TaskIcon } from './images/TaskIcon';
import { HomeNavigationItem } from './HomeNavigationItem';

import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	overview: {
		id: 'home.navigation.overview',
		defaultMessage: 'Overview',
		description: 'Link to an overview of the users work',
	},
	recent: {
		id: 'home.navigation.recent',
		defaultMessage: 'Recent',
		description: 'Link to a list of the users recent work',
	},
	starred: {
		id: 'home.navigation.starred',
		defaultMessage: 'Starred',
		description: 'Link to a list of pages the user starred',
	},
	watching: {
		id: 'home.navigation.watching',
		defaultMessage: 'Watching',
		description: 'Link to a list of pages the user is watching',
	},
	drafts: {
		id: 'home.navigation.drafts',
		defaultMessage: 'Drafts',
		description: 'Link to a list of the users drafts',
	},
	goToPersonalSpace: {
		id: 'home.navigation.go-personal-space',
		defaultMessage: 'Your space',
		description:
			"Text of a button which links to the user's personal space. Displayed underneath avatar and username as part of profile card.",
	},
	tasks: {
		id: 'home.navigation.tasks-home',
		defaultMessage: 'Tasks',
		description: 'Link to a list of tasks assigned to the user or created by them',
	},
	side: {
		id: 'home.navigation.side',
		defaultMessage: 'Side',
		description: 'Aria label for the side navigation',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const NavigationLinksSection = styled.nav`
	width: 100%;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const Card = styled.div<{
	rightSidebarCompensation;
}>`
	align-items: center;
	/* Enforce width only if nav isn't collapsed */
	max-width: 240px;
	position: relative;
	min-width: calc(240px - 240px * var(--homeNavIsCollapsed));
	--homeIsLegacy: 0;
	min-width: calc(240px - 240px * var(--homeNavIsCollapsed));
	a:selected svg {
		fill: ${token('color.link', B400)};
	}

	/* No shadow or background if nav is collapsed */
	@media (max-width: ${NAV_COLLAPSE_BREAKPOINT_PX}px) {
		background-color: transparent;
		box-shadow: none;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const SpotlightContainer = styled.div`
	position: absolute;
	top: ${token('space.negative.050', '-4px')};
	left: ${token('space.negative.050', '-4px')};
	padding: ${token('space.050', '4px')};
	height: calc(100% - 8px);
	width: 100%;
	pointer-events: none;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const LinksSectionList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const LinksSectionListItem = styled.li`
	margin: 0;
	padding: 0;
`;

export const HomeNavigation = ({ rightSidebarCompensation }) => {
	const { formatMessage } = useIntl();
	return (
		<Card data-testid="HomeNavigation-card" rightSidebarCompensation={rightSidebarCompensation}>
			<SpotlightTarget name="home-navigation">
				<SpotlightContainer />
			</SpotlightTarget>
			<NavigationLinksSection aria-label={formatMessage(i18n.side)}>
				<LinksSectionList>
					<LinksSectionListItem>
						<Tooltip content={<FormattedMessage {...i18n.overview} />}>
							<HomeNavigationItem view={View.OVERVIEW} icon={<HomeIcon label="" />}>
								<FormattedMessage {...i18n.overview} />
							</HomeNavigationItem>
						</Tooltip>
					</LinksSectionListItem>
					<LinksSectionListItem>
						<Tooltip content={<FormattedMessage {...i18n.recent} />}>
							<HomeNavigationItem view={View.RECENT} icon={<RecentIcon label="" />}>
								<FormattedMessage {...i18n.recent} />
							</HomeNavigationItem>
						</Tooltip>
					</LinksSectionListItem>
					<LinksSectionListItem>
						<Tooltip content={<FormattedMessage {...i18n.starred} />}>
							<HomeNavigationItem view={View.STARRED} icon={<StarIcon label="" />}>
								<FormattedMessage {...i18n.starred} />
							</HomeNavigationItem>{' '}
						</Tooltip>
					</LinksSectionListItem>
					{!fg('confluence_live_pages_ab_test_opted_in') && (
						<LinksSectionListItem>
							<Tooltip content={<FormattedMessage {...i18n.drafts} />}>
								<HomeNavigationItem view={View.DRAFTS} icon={<DraftIcon label="" />}>
									<FormattedMessage {...i18n.drafts} />
								</HomeNavigationItem>{' '}
							</Tooltip>
						</LinksSectionListItem>
					)}
					<LinksSectionListItem>
						<Tooltip content={<FormattedMessage {...i18n.tasks} />}>
							<HomeNavigationItem
								view={View.TASKS}
								icon={<TaskIcon label="" />}
								render={(props) => <TaskNavigationItem {...props} />}
							/>{' '}
						</Tooltip>
					</LinksSectionListItem>
				</LinksSectionList>
			</NavigationLinksSection>
			<LazyLeftPanelExtendedItems />
		</Card>
	);
};
